import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '@vmgate/common';
import type { ValidationLogResponse } from '@vmgate/common';
import { LoadingScreen } from '../components/common/LoadingScreen';
import { ErrorState } from '../components/common/ErrorState';
import { PaginationControls } from '../components/common/PaginationControls';
import { StatusBadge } from '../components/common/StatusBadge';
import { formatDateTime } from '../utils/format';

const RESULT_OPTIONS = ['SUCCESS', 'FAILED', 'REVOKED', 'NOT_FOUND'];

export function ValidationLogsPage() {
  const [page, setPage] = useState(1);
  const [resultFilter, setResultFilter] = useState('ALL');
  const [licenseFilter, setLicenseFilter] = useState('');

  const validationLogsQuery = useQuery<ValidationLogResponse>({
    queryKey: ['validation-logs', page, resultFilter, licenseFilter],
    queryFn: () =>
      adminApi.getValidationLogs({
        page,
        result: resultFilter === 'ALL' ? undefined : resultFilter,
        license_id: licenseFilter || undefined
      }),
    placeholderData: (previousData) => previousData
  });
  const { data, isLoading, isError, refetch, isFetching } = validationLogsQuery;

  if (isLoading) {
    return <LoadingScreen message="Loading validation logs..." />;
  }

  if (isError || !data) {
    return <ErrorState message="Unable to load validation logs." onRetry={() => refetch()} />;
  }

  const logs = data.logs;

  return (
    <div className="surface surface-padding" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <header>
        <h2 style={{ margin: 0 }}>Validation logs</h2>
        <div className="text-muted">Latest device validation attempts recorded by VMGate.</div>
      </header>

      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <label className="text-muted" style={{ display: 'block', marginBottom: '0.4rem' }}>
            Result
          </label>
          <select
            value={resultFilter}
            onChange={(event) => {
              setResultFilter(event.target.value);
              setPage(1);
            }}
            style={selectStyle}
          >
            <option value="ALL">All results</option>
            {RESULT_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-muted" style={{ display: 'block', marginBottom: '0.4rem' }}>
            License ID
          </label>
          <input
            type="text"
            value={licenseFilter}
            onChange={(event) => {
              setLicenseFilter(event.target.value);
              setPage(1);
            }}
            placeholder="LT-CUST-..."
            style={selectStyle}
          />
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>License</th>
              <th>Result</th>
              <th>Fingerprint</th>
              <th>IP</th>
              <th>Message</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <tr key={log.id}>
                <td>{formatDateTime(log.validation_timestamp)}</td>
                <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{log.license_id}</td>
                <td>
                  <StatusBadge status={log.validation_result} />
                </td>
                <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{log.device_fingerprint_hash}</td>
                <td>{log.ip_address || '—'}</td>
                <td>{log.error_message || '—'}</td>
              </tr>
            ))}
            {logs.length === 0 && (
              <tr>
                <td colSpan={6} className="text-muted" style={{ textAlign: 'center', padding: '1rem' }}>
                  No validation activity recorded yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <PaginationControls
        page={data.current_page}
        totalPages={data.pages ?? 1}
        onPageChange={setPage}
        disabled={isFetching}
      />
    </div>
  );
}

const selectStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)',
  borderRadius: '10px',
  padding: '0.6rem 0.8rem',
  fontSize: '0.95rem',
  background: '#fff'
};

